"use client";

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../UI/Button/Button";
import Card from "../UI/Card/Card";
import Alert from "../UI/Alert/Alert";
import {
  getMappedPatientData,
  getAICVDResults,
} from "../../services/data-service";
import Loader from "../UI/Loader/Loader";
import "./ResultsPage.css";
import Disclaimer from "./Disclaimer/Disclaimer";
import ProtocolTable from "./ProtocolTable/ProtocolTable";
function ResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uhid = searchParams.get("uhid");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apidata, setAPI] = useState();

  useEffect(() => {
    async function loadResults() {
      if (!uhid) {
        setError("UHID is required");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const { mappedData } = await getMappedPatientData(uhid);
        const data = await getAICVDResults(uhid, mappedData);
        if (data) {
          const report = data[0];
          const prediction = report.Prediction;
          let topRiskContributors = prediction.HeartRisk.TopRiskContributors;

          setAPI({
            optimal: prediction.HeartRisk.Acceptable,
            risk: prediction.HeartRisk.Risk,
            score: prediction.HeartRisk.Score,
            Ratio: prediction.HeartRisk.Ratio,
            topRiskContributors: topRiskContributors,
            department: prediction.MedicalProtocol.Referral.Department,
            urgency: prediction.MedicalProtocol.Referral.Urgency,
            advice: prediction.MedicalProtocol.Management.GeneralAdvice,
            medication: prediction.MedicalProtocol.Medication,
            diagnosticsAndImagingRecommended:
              prediction.MedicalProtocol.DiagnosticsAndImagingRecommended,
            labInvestigationRecommended:
              prediction.MedicalProtocol.LabInvestigationRecommended,
            repeatVisitComment: prediction.ManagementRepeatVisit,
            repeatVisitDuration:
              prediction.MedicalProtocol.Management.RepeatVisit.Duration,
            repeatVisitUnits:
              prediction.MedicalProtocol.Management.RepeatVisit.Unit,
            loaderStatus: false,
            aicvd_note: report.aicvd_note,
            treatment_goals: report.treatment_goals,
            AICVD_Cardiac_Risk_Score: report.AICVD_Cardiac_Risk_Score,
            note: report.note,
            riskProfile: report.risk_profile,
          });
          setLoading(false);
        }
      } catch (err) {
        console.error("Results fetch error:", err);
        setError("Error fetching AICVD risk assessment results");
      } finally {
        setLoading(false);
      }
    }

    loadResults();
  }, [uhid]);

  const getRiskCardColor = () => {
    if (apidata && apidata.risk === "High Risk") return "risk-high-color";
    else if (apidata && apidata.risk === "Moderate Risk")
      return "risk-medium-color";
    else return "risk-low-color";
  };

  if (loading) {
    return (
      <main className="results-container">
        <Card className="loading-card">
          <div className="loading-content">
            <Loader />
            <p>Loading AICVD risk assessment results...</p>
          </div>
        </Card>
      </main>
    );
  }

  if (error) {
    return (
      <main className="results-container">
        <Card className="error-card">
          <div className="error-content">
            <Alert variant="destructive" title="Error" description={error} />
            <div className="error-actions">
              <Button onClick={() => navigate("/")}>Return to Home</Button>
            </div>
          </div>
        </Card>
      </main>
    );
  }

  return (
    <div className="results-container">
      {apidata && (
        <div className="new-report-container">
          <div className="new-report-card">
            <h2 className="report-card-title">
              AI Calculated Risk Score Based on Your Inputs
            </h2>
            <div className="risk-section">
              <div className="risk-cards">
                <div className="risk-card">
                  <h3 className="risk-card-title">Risk Status</h3>
                  <div className={`${getRiskCardColor()} risk-highlight`}>
                    {apidata.risk}
                  </div>
                </div>
                <div className="risk-card">
                  <h3 className="risk-card-title">Patient Score</h3>
                  <p className="risk-value">{apidata.score}</p>
                </div>
                <div className="risk-card">
                  <h3 className="risk-card-title">Acceptable Score</h3>
                  <p className="risk-value">{apidata.optimal}</p>
                </div>
                <div className="risk-card">
                  <h3 className="risk-card-title">Risk Profile</h3>
                  <p className="risk-value">{apidata.riskProfile}x</p>
                </div>
              </div>
              <p className="note-items">{apidata.note}</p>
              <p className="note-items">
                Note: <span>{apidata.aicvd_note}</span>
              </p>
            </div>
          </div>

          <div className="new-report-card">
            <h2 className="report-card-title">Top Modifiable Risk Factors</h2>
            <div className="risk-section">
              <div className="risk-cards">
                {apidata.topRiskContributors !== null ? (
                  apidata.topRiskContributors?.map((each, index) => (
                    <div className="risk-card" key={index}>
                      <p className="risk-value">
                        {each === "bmi" ? "BMI" : each}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="no-data">No specific risk factors identified</p>
                )}
              </div>
            </div>
          </div>

          <div className="new-report-card">
            <h2 className="report-card-title">AI Recommended Protocol</h2>
            <ProtocolTable data={apidata} />
          </div>

          <div className="new-report-card">
            <h2 className="report-card-title">Disclaimer</h2>
            <Disclaimer disclaimers={apidata.disclaimer} />
          </div>

          <div className="report-actions">
            <Button
              variant="outline"
              onClick={() => navigate(`/review?uhid=${uhid}`)}
            >
              Back to Review
            </Button>
            <Button
              onClick={() => navigate("/home")}
              className="new-assessment-button"
            >
              Start New Assessment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResultsPage;
